import React, { useEffect, useState } from "react";
import { Outlet, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "../components/Header";
import { Stepper } from "../components/Stepper";
import { StaleExclusionBanner } from "../components/StaleExclusionBanner";
import { FirstRunRenameBanner } from "../components/FirstRunRenameBanner";
import { TraceDrawer } from "../components/TraceDrawer";
import { StepSkeleton } from "../components/StepSkeleton";
import { useWorkflow } from "../workflow/WorkflowContext";
import { stepPath, WORKFLOW_BASE } from "../workflow/workflowRoutes";
import { useUrlCalcOpen } from "../workflow/useUrlCalcOpen";
import { CalcIdentityHeader } from "../components/CalcIdentityHeader";

/**
 * WorkflowLayout (v0.6 nav refactor) — the persistent chrome for the
 * URL-routed 6-step workflow. Renders Header + Stepper + banners ONCE
 * and swaps only the active step through <Outlet/>. While the
 * authoritative calc re-hydrates from the backend on a hard load it
 * shows a skeleton so the transition is clean, never a blank flash.
 *
 * The backend calc remains the single source of truth (re-fetched in
 * WorkflowContext on boot); the URL only records WHICH step is active.
 */

/** Index redirect for /workflow → the derived active step's slug,
 *  preserving any query string (?calc_id deep-links). */
export const WorkflowIndexRedirect = () => {
  const { activeStep } = useWorkflow();
  const loc = useLocation();
  // A1-93 — never carries `calc_id` on: WorkflowLayout has read it already,
  // and a late commit of this redirect must not put it back in the address.
  const params = new URLSearchParams(loc.search);
  params.delete("calc_id");
  const rest = params.toString();
  return <Navigate to={{ pathname: stepPath(activeStep), search: rest ? `?${rest}` : "" }} state={loc.state} replace />;
};

export default function WorkflowLayout() {
  const { clients, booting, calc, activeStep } = useWorkflow();
  const { t } = useTranslation();
  const [drillLine, setDrillLine] = useState(null);
  // A1-93 — a calc link that was NOT opened is said so, in one line.
  const [urlCalcNotice, setUrlCalcNotice] = useState(null);
  const footerRights = t("ui.footer.rights");

  // Deep-link: `?calc_id=<id>` lands directly on the calc's Review (or
  // Freeze, if already signed/frozen) step — see workflow/useUrlCalcOpen.js
  // (A1-93: history rules, leftovers, the v0.6.6/v0.6.7 client race notes).
  useUrlCalcOpen(setUrlCalcNotice);

  // A2-581 lead 9: an admin-only page opened by someone else lands here
  // (auth/AuthGate); say so once, and take the mark off the address so a
  // reload does not say it again.
  const loc = useLocation();
  const navigate = useNavigate();
  const [forbidden, setForbidden] = useState(false);
  useEffect(() => {
    // the bare /workflow address redirects to the step, carrying the mark on
    if (!loc.state?.forbidden || loc.pathname.replace(/\/$/, "") === WORKFLOW_BASE) return;
    setForbidden(true);
    navigate({ pathname: loc.pathname, search: loc.search }, { replace: true, state: null });
  }, [loc.state, loc.pathname, loc.search, navigate]);

  return (
    <div data-testid="dashboard" className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Header />
      <FirstRunRenameBanner clients={clients} />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-8 py-8 space-y-6">
        {urlCalcNotice && (
          <div data-testid="url-calc-notice" data-kind={urlCalcNotice.kind} role="status"
            className="flex items-center justify-between gap-3 border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 rounded-md px-4 py-2 text-xs text-amber-900 dark:text-amber-100">
            <span>{urlCalcNotice.kind === "kept"
              ? t("ui.url_calc.kept", { id: urlCalcNotice.id.slice(0, 8), defaultValue: `The link pointed to calculation ${urlCalcNotice.id.slice(0, 8)}, but this tab already had another calculation open, so that one is still shown. Open the linked one from History if you meant it.` })
              : t("ui.url_calc.failed", { id: urlCalcNotice.id.slice(0, 8), defaultValue: `Calculation ${urlCalcNotice.id.slice(0, 8)} could not be opened — it may have been deleted, or the connection failed.` })}</span>
            <button type="button" data-testid="url-calc-notice-dismiss" onClick={() => setUrlCalcNotice(null)}
              className="underline decoration-dotted">{t("ui.url_calc.dismiss", { defaultValue: "Dismiss" })}</button>
          </div>
        )}
        {forbidden && (
          <div data-testid="forbidden-notice" role="status"
            className="flex items-center justify-between gap-3 border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 rounded-md px-4 py-2 text-xs text-amber-900 dark:text-amber-100">
            <span>{t("ui.forbidden_notice", { defaultValue: "That page is for admins only, so you were brought back here. Ask an admin to make the change." })}</span>
            <button type="button" data-testid="forbidden-notice-dismiss" onClick={() => setForbidden(false)}
              className="underline decoration-dotted">{t("ui.url_calc.dismiss", { defaultValue: "Dismiss" })}</button>
          </div>
        )}
        {/* A2-474 — every step page has one h1 (visually hidden: the stepper shows it). */}
        <h1 className="sr-only" data-testid="workflow-page-title">
          {t(`ui.stepper.step${activeStep}`, { defaultValue: `Step ${activeStep}` })}
        </h1>
        <Stepper />
        <StaleExclusionBanner />
        {/* A1-93 (2) — which calculation is on screen, when and under which
            app version it ran, what it used, and whether it is stale. */}
        {!booting && calc && activeStep >= 3 && <CalcIdentityHeader />}
        {booting ? <StepSkeleton /> : <Outlet context={{ onDrill: setDrillLine }} />}
      </main>
      <TraceDrawer line={drillLine} onClose={() => setDrillLine(null)} />
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 mt-12 px-6 py-4 text-xs text-slate-500 dark:text-slate-400 flex flex-wrap items-center justify-between gap-2">
        <span>
          <span className="text-teal-700 dark:text-teal-300 font-medium">GD VAT</span> — {footerRights}
        </span>
      </footer>
    </div>
  );
}
